<template>
  <section id="hero" class="hero" :class="{ 'dark-mode': isDarkMode }">
    <div class="hero-content">
      <h1 ref="heroName">{{ $t("hero.name") }}</h1>
      <h2 ref="heroTitle" class="typing-text">{{ dynamicTitle }}</h2>
      <p ref="heroDesc">{{ $t("hero.description") }}</p>
      <a href="#about" class="cta-button">{{ $t("hero.cta") }}</a>
    </div>
  </section>
</template>

<script>
import { watch } from "vue";
import gsap from "gsap";

export default {
  name: "HeroComponent",
  data() {
    return {
      dynamicTitle: "",
      titleIndex: 0,
      charIndex: 0,
      isDeleting: false,
      typingTimeout: null,
    };
  },
  mounted() {
    gsap.from(this.$refs.heroName, { opacity: 0, y: -50, duration: 1 });
    gsap.from(this.$refs.heroTitle, {
      opacity: 0,
      y: -50,
      delay: 0.3,
      duration: 1,
    });
    gsap.from(this.$refs.heroDesc, {
      opacity: 0,
      y: -50,
      delay: 0.6,
      duration: 1,
    });

    this.typeEffect();

    watch(
      () => this.$i18n.locale,
      () => {
        this.resetTypingEffect();
        this.typeEffect();
      }
    );
  },
  computed: {
    isDarkMode() {
      return this.$store.getters.isDarkMode;
    },
  },
  methods: {
    typeEffect() {
      const titles = this.$tm("hero.titles");
      let currentTitle = titles[this.titleIndex];

      if (this.isDeleting) {
        this.dynamicTitle = currentTitle.substring(0, this.charIndex - 1);
        this.charIndex--;
      } else {
        this.dynamicTitle = currentTitle.substring(0, this.charIndex + 1);
        this.charIndex++;
      }

      let speed = this.isDeleting ? 50 : 100;

      if (!this.isDeleting && this.charIndex === currentTitle.length) {
        speed = 2000;
        this.isDeleting = true;
      } else if (this.isDeleting && this.charIndex === 0) {
        this.isDeleting = false;
        this.titleIndex = (this.titleIndex + 1) % titles.length;
        speed = 500;
      }

      clearTimeout(this.typingTimeout);
      this.typingTimeout = setTimeout(this.typeEffect, speed);
    },
    resetTypingEffect() {
      clearTimeout(this.typingTimeout);
      this.dynamicTitle = "";
      this.charIndex = 0;
      this.isDeleting = false;
      this.titleIndex = 0;
    },
  },
};
</script>

<style scoped>
.dark-mode .hero {
  background: linear-gradient(120deg, #4e4b67, #1f1f35, #06634f);
  background-size: 400% 400%;
  animation: gradientBG 9s ease infinite;
}

/* En el modo claro */
.hero {
  background: linear-gradient(120deg, #42b983, #5c5c8f, #e4e4e4);
  background-size: 400% 400%;
  animation: gradientBG 6s ease infinite;
}

.hero {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  text-align: center;
  padding: 20px;
}

@keyframes gradientBG {
  0% {
    background-position: 0% 50%;
  }

  50% {
    background-position: 100% 50%;
  }

  100% {
    background-position: 0% 50%;
  }
}

.hero-content {
  max-width: 800px;
}

h1 {
  font-size: 4rem;
  color: white;
  font-weight: bold;
  margin-bottom: 10px;
}

h2 {
  font-size: 2rem;
  margin-bottom: 20px;
  color: #fff;
  overflow: hidden;
  white-space: nowrap;
  width: fit-content;
  position: relative;
  margin: 0 auto;
  text-align: center;
}

h2::after {
  content: "|";
  display: inline-block;
  width: 8px;
  animation: blink 0.7s infinite;
}

@keyframes blink {
  50% {
    opacity: 0;
  }
}

p {
  font-size: 1.2rem;
  color: white;
  margin-bottom: 30px;
}

.cta-button {
  padding: 10px 20px;
  background-color: #42b983;
  color: white;
  text-decoration: none;
  border-radius: 24px;
  font-size: 1rem;
  transition: transform 0.3s ease;
  will-change: transform;
}

.cta-button:hover {
  transform: scale(1.1);
}

@media (max-width: 768px) {
  h1 {
    font-size: 3rem;
  }

  h2 {
    font-size: 1.5rem;
  }

  p {
    font-size: 1rem;
  }
}
</style>
